import { useCallback, useEffect, useMemo, useState } from 'react';
import { Anchor, Ban, Link2 } from 'lucide-react';
import { NoteDisclosure } from '@/components/Disclosure';
import { Can } from '@/auth/guards';
import { Hint } from '@/components/Hint';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  getCarrierCharges,
  setReceiptDuty,
  updateCarrierCharge,
  type CarrierCharge,
  type OrdersOverview,
  type OverviewReceiving,
} from '@/api/orders';
import { fmtDate, money } from './lib';
import {
  AcotReceivingLink, JourneyName, RowAction, UploadDocumentButton,
  WorkEmpty, WorkFootnote, WorkLoading, WorkNotice, WorkRow, WorkSection,
} from './workLib';

/**
 * Duties — the two halves of the same join, side by side:
 *   1. Carrier bills (DHL) that arrived as money with no shipment — the payment
 *      receipt / statement emails carry an invoice# and an amount but no AWB.
 *      Each needs attributing to the receiving it cleared, or dismissing (bond
 *      renewals are not product cost).
 *   2. Receivings holding open for a duty bill (duty_expected, nothing
 *      attributed) — international non-DDP shipments whose balance is blocked
 *      until the carrier bill lands. Waive when no bill is coming.
 * Attribution folds the amount into the receiving's balance: it distributes
 * across the arrived lines exactly like freight billed on its own invoice.
 */
export function DutiesView({ overview, onChanged, onOpenJourney }: {
  overview: OrdersOverview;
  onChanged: () => void;
  onOpenJourney: (orderId: string) => void;
}) {
  const [charges, setCharges] = useState<CarrierCharge[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  // Per-charge picked receiving (receiptId) before Attach is clicked.
  const [picked, setPicked] = useState<Record<string, string>>({});

  const loadCharges = useCallback(async () => {
    setLoadError(null);
    try {
      setCharges(await getCarrierCharges('unattributed'));
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load carrier charges');
    }
  }, []);

  useEffect(() => { loadCharges(); }, [loadCharges]);

  const awaiting = useMemo(
    () => overview.receivings
      .filter((r) => !r.balancedAt && r.dutyExpected === true && r.carrierCharges.length === 0)
      .sort((a, b) => b.receivingId - a.receivingId),
    [overview]
  );

  // Balanced with the duty still unattributed: the hold-open never fired
  // (balanced before duty_expected existed, or force-marked past it), so no
  // list showed it — Mintay 32738 sat reconciled with an expected duty and
  // zero charges, and the Waive button that resolves it lived in a view that
  // filtered balanced rows out. Waive, or attach the late bill (which warns
  // and re-opens the balance).
  const watch = useMemo(
    () => overview.receivings
      .filter((r) => r.balancedAt && r.dutyExpected === true && r.carrierCharges.length === 0)
      .sort((a, b) => b.receivingId - a.receivingId),
    [overview]
  );

  // Never assessed, on a vendor that HAS billed duty before. `duty_expected` is
  // three-valued and only an explicit true holds a receiving open, so null is
  // not "no duty due" — it is nobody having looked. The vendor set is derived
  // server-side from actual duty history, which is what keeps this list at a
  // handful rather than every domestic receiving ever mirrored.
  const unassessed = overview.dutyUnassessed ?? [];

  // Attachment candidates: receivings holding open for a duty first (the
  // likely homes), then every other unbalanced mirrored receiving.
  const candidates = useMemo(() => {
    const open = overview.receivings.filter((r) => !r.balancedAt);
    const holding = open.filter((r) => r.dutyExpected === true && r.carrierCharges.length === 0);
    const rest = open.filter((r) => !(r.dutyExpected === true && r.carrierCharges.length === 0));
    return [...holding, ...rest];
  }, [overview]);

  const attributedCount = useMemo(
    () => overview.receivings.reduce((acc, r) => acc + r.carrierCharges.length, 0),
    [overview]
  );

  if (charges === null && !loadError) {
    return <WorkLoading>Loading carrier bills…</WorkLoading>;
  }

  const pool = charges ?? [];
  if (!pool.length && !awaiting.length && !watch.length && !unassessed.length) {
    return (
      <WorkEmpty>
        No carrier bills awaiting attribution and no receivings holding open for one.
        {attributedCount > 0 && ` ${attributedCount} attributed.`}
      </WorkEmpty>
    );
  }

  const candidateLabel = (r: OverviewReceiving) =>
    `#${r.receivingId} ${r.vendor}${r.label ? ` — ${r.label}` : ''}${r.dutyExpected === true && r.carrierCharges.length === 0 ? ' (awaiting duty)' : ''}`;

  const setDuty = (r: { receiptId: string }, expected: boolean, note: string) => async () => {
    setBusyId(r.receiptId);
    try {
      await setReceiptDuty(r.receiptId, { expected, note });
      onChanged();
    } finally {
      setBusyId(null);
    }
  };

  // Every verdict and attribution below is orders:write:duties on the server;
  // the upload buttons beside them carry their own documents code.
  const waive = (r: OverviewReceiving, hint: string) => (
    <Can code="orders:write:duties">
      <Hint content={hint}>
        <RowAction
          variant="quiet"
          icon={<Ban className="h-3 w-3" />}
          busy={busyId === r.receiptId}
          disabled={busyId === r.receiptId}
          onClick={setDuty(r, false, 'Waived from the Duties view — no carrier bill expected')}
        >
          Waive
        </RowAction>
      </Hint>
    </Can>
  );

  return (
    <div>
      {loadError && (
        <WorkNotice tone="danger" onRetry={() => void loadCharges()}>{loadError}</WorkNotice>
      )}

      {pool.length > 0 && (
        <WorkSection title="Carrier bills" count={pool.length}>
          {pool.map((c) => (
            <WorkRow
              key={c.id}
              scrollKey={`charge:${c.id}`}
              title={<><span className="font-semibold uppercase">{c.carrier}</span> {c.carrierInvoiceNo}</>}
              detail={c.note ? <NoteDisclosure text={c.note} /> : undefined}
              actions={
                <Can code="orders:write:duties">
                  <Select
                    value={picked[c.id] ?? ''}
                    onValueChange={(v) => setPicked((prev) => ({ ...prev, [c.id]: v }))}
                  >
                    <SelectTrigger className="w-56 text-xs">
                      <SelectValue placeholder="Pick the receiving it cleared…" />
                    </SelectTrigger>
                    <SelectContent>
                      {candidates.map((r) => (
                        <SelectItem key={r.receiptId} value={r.receiptId} className="text-xs">
                          {candidateLabel(r)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Hint content="Attribute this bill to the picked receiving.">
                    <RowAction
                      icon={<Link2 className="h-3 w-3" />}
                      busy={busyId === c.id}
                      disabled={!picked[c.id] || busyId === c.id}
                      onClick={async () => {
                        setBusyId(c.id);
                        try {
                          await updateCarrierCharge(c.id, { attach: { receipt_id: picked[c.id] } });
                          await loadCharges();
                          onChanged();
                        } finally {
                          setBusyId(null);
                        }
                      }}
                    >
                      Attach
                    </RowAction>
                  </Hint>
                  <Hint content="Not product cost; remove it from the queue.">
                    <RowAction
                      variant="danger"
                      icon={<Ban className="h-3 w-3" />}
                      disabled={busyId === c.id}
                      onClick={async () => {
                        setBusyId(c.id);
                        try {
                          await updateCarrierCharge(c.id, { dismiss: true });
                          await loadCharges();
                          onChanged();
                        } finally {
                          setBusyId(null);
                        }
                      }}
                    >
                      Dismiss
                    </RowAction>
                  </Hint>
                </Can>
              }
            >
              {c.invoiceDate && (
                <span className="text-xs text-muted-foreground tabular-nums">{fmtDate(c.invoiceDate)}</span>
              )}
              <span className="text-sm font-medium tabular-nums">
                {c.amount != null ? money(c.amount, c.currency) : 'amount unknown'}
              </span>
              {c.awb && (
                <Hint content="Air waybill on the bill; matches the shipment's tracking number.">
                  <span className="text-xs text-muted-foreground">AWB {c.awb}</span>
                </Hint>
              )}
            </WorkRow>
          ))}
        </WorkSection>
      )}

      {awaiting.length > 0 && (
        <WorkSection title="Awaiting duty" count={awaiting.length}>
          {awaiting.map((r) => (
            <WorkRow
              key={r.receiptId}
              scrollKey={`awaiting:${r.receiptId}`}
              title={<JourneyName vendor={r.vendor} label={r.label} onOpen={() => onOpenJourney(r.orderId)} />}
              detail={r.dutyNote ? <NoteDisclosure text={r.dutyNote} /> : undefined}
              actions={
                <>
                  <UploadDocumentButton
                    orderId={r.orderId}
                    docHint="duty_invoice"
                    label="Upload duty bill"
                    onUploaded={onChanged}
                  />
                  {waive(r, 'No duty bill is coming; clears the hold.')}
                </>
              }
            >
              <AcotReceivingLink receivingId={r.receivingId} />
            </WorkRow>
          ))}
        </WorkSection>
      )}

      {watch.length > 0 && (
        <WorkSection title="Balanced without duty" count={watch.length}>
          {watch.map((r) => (
            <WorkRow
              key={r.receiptId}
              scrollKey={`watch:${r.receiptId}`}
              title={<JourneyName vendor={r.vendor} label={r.label} onOpen={() => onOpenJourney(r.orderId)} />}
              detail={r.dutyNote ? <NoteDisclosure text={r.dutyNote} /> : undefined}
              actions={
                <>
                  <UploadDocumentButton
                    orderId={r.orderId}
                    docHint="duty_invoice"
                    label="Upload duty bill"
                    onUploaded={onChanged}
                  />
                  {waive(r, 'No duty bill is coming.')}
                </>
              }
            >
              <AcotReceivingLink receivingId={r.receivingId} />
            </WorkRow>
          ))}
        </WorkSection>
      )}

      {unassessed.length > 0 && (
        <WorkSection title="No duty verdict" count={unassessed.length}>
          {unassessed.map((r) => (
            <WorkRow
              key={r.receiptId}
              scrollKey={`unassessed:${r.receiptId}`}
              title={<JourneyName vendor={r.vendor} label={null} onOpen={() => onOpenJourney(r.orderId)} />}
              actions={
                <Can code="orders:write:duties">
                  <Hint content="A duty bill is coming; holds the balance open until it lands.">
                    <RowAction
                      icon={<Anchor className="h-3 w-3" />}
                      busy={busyId === r.receiptId}
                      disabled={busyId === r.receiptId}
                      onClick={setDuty(r, true, 'Duty expected — set from the Duties view')}
                    >
                      Duty expected
                    </RowAction>
                  </Hint>
                  <Hint content="No separate duty bill on this one.">
                    <RowAction
                      variant="quiet"
                      icon={<Ban className="h-3 w-3" />}
                      disabled={busyId === r.receiptId}
                      onClick={setDuty(r, false, 'No duty expected — set from the Duties view')}
                    >
                      No duty
                    </RowAction>
                  </Hint>
                </Can>
              }
            >
              <AcotReceivingLink receivingId={r.receivingId} />
              {r.dateCreated && (
                <span className="text-xs tabular-nums text-muted-foreground">
                  received {fmtDate(r.dateCreated)}
                </span>
              )}
            </WorkRow>
          ))}
        </WorkSection>
      )}

      {attributedCount > 0 && (
        <WorkFootnote>
          {attributedCount} carrier charge{attributedCount === 1 ? '' : 's'} attributed and feeding the balance.
        </WorkFootnote>
      )}
    </div>
  );
}
